<script setup>
import { computed, onMounted } from 'vue';
import { useI18n } from 'vue-i18n';
import { useRouter } from 'vue-router';
import { useMapGetter, useStore } from 'dashboard/composables/store';
import { useAccount } from 'dashboard/composables/useAccount';
import Icon from 'next/icon/Icon.vue';
import { Badge } from 'dashboard/components-next/polaris/ui/badge';
import { Button } from 'dashboard/components-next/polaris/ui/button';
import {
  Item,
  ItemActions,
  ItemContent,
  ItemDescription,
  ItemMedia,
  ItemTitle,
} from 'dashboard/components-next/polaris/ui/item';
import SettingsLayout from '../SettingsLayout.vue';
import BaseSettingsHeader from '../components/BaseSettingsHeader.vue';
import {
  CHANNEL_TYPES,
  channelTypeOfInbox,
  isCreationAvailable,
  isCreationListed,
} from './channelTypes';

// "All channels" (phase 9.6, D-S3 / R2-9-3 = A; the kit's ChannelDirectory): one card per channel type, with its
// brand icon (the woot's, on purpose), whether it is connected and how many inboxes, and the one next step:
// "Manage" opens the type's page, "Set up" goes straight to the new-inbox form of that type. It is also step 1 of
// a new inbox (settings_inbox_new comes here).
const { t } = useI18n();
const store = useStore();
const router = useRouter();
const { currentAccount } = useAccount();

const inboxes = useMapGetter('inboxes/getInboxes');
const uiFlags = useMapGetter('inboxes/getUIFlags');

const features = computed(() => currentAccount.value?.features || {});

const cards = computed(() =>
  CHANNEL_TYPES.map(type => {
    const upper = type.key.toUpperCase();
    const count = inboxes.value.filter(
      inbox => channelTypeOfInbox(inbox) === type.key
    ).length;
    const forms = type.create.filter(isCreationListed);
    const available = forms.filter(key =>
      isCreationAvailable(key, features.value)
    );
    return {
      ...type,
      name: t(`SETTINGS_CHANNELS.TYPES.${upper}.NAME`),
      sentence: t(`SETTINGS_CHANNELS.TYPES.${upper}.SENTENCE`),
      count,
      // one form: "Set up" opens it; several (social, voice): the type page lets you pick
      setUpForm: available.length === 1 ? available[0] : null,
      available: available.length > 0,
    };
  })
);

const manage = card =>
  router.push({
    name: 'settings_channel_type',
    params: { channelType: card.key },
  });

const setUp = card => {
  if (!card.setUpForm) {
    manage(card);
    return;
  }
  router.push({
    name: 'settings_inboxes_page_channel',
    params: { sub_page: card.setUpForm },
  });
};

onMounted(() => {
  store.dispatch('inboxes/get');
});
</script>

<template>
  <SettingsLayout :is-loading="uiFlags.isFetching && !inboxes.length">
    <template #header>
      <BaseSettingsHeader
        :title="t('SETTINGS_CHANNELS.TYPES.ALL.NAME')"
        :description="t('INBOX_MGMT.DESCRIPTION')"
        :link-text="t('INBOX_MGMT.LEARN_MORE')"
        feature-name="inboxes"
      />
    </template>
    <template #body>
      <!-- Two across, three when there is room: the room of the list, not the viewport (the kit's rule). -->
      <div class="@container">
        <ul
          role="list"
          :aria-label="t('SETTINGS_CHANNELS.CARDS_LABEL')"
          class="m-0 grid list-none gap-3 p-0 @2xl:grid-cols-2 @6xl:grid-cols-3"
        >
          <li v-for="card in cards" :key="card.key" class="min-w-0">
            <Item
              variant="outline"
              size="sm"
              class="h-full flex-nowrap items-start"
              data-channel-card
            >
              <ItemMedia variant="icon">
                <Icon :icon="card.icon" class="size-4" />
              </ItemMedia>
              <ItemContent class="min-w-0 gap-1">
                <ItemTitle class="flex-wrap">
                  {{ card.name }}
                  <Badge v-if="card.count" variant="success">
                    {{
                      t(
                        'SETTINGS_CHANNELS.CONNECTED',
                        { count: card.count },
                        card.count
                      )
                    }}
                  </Badge>
                  <Badge v-else variant="outline">
                    {{
                      card.available
                        ? t('SETTINGS_CHANNELS.NOT_CONNECTED')
                        : t('SETTINGS_CHANNELS.UNAVAILABLE')
                    }}
                  </Badge>
                </ItemTitle>
                <ItemDescription
                  class="line-clamp-none text-wrap type-polaris-caption"
                >
                  {{ card.sentence }}
                </ItemDescription>
              </ItemContent>
              <ItemActions>
                <Button
                  v-if="card.count"
                  variant="outline"
                  size="sm"
                  @click="manage(card)"
                >
                  {{ t('SETTINGS_CHANNELS.MANAGE') }}
                </Button>
                <Button
                  v-else
                  variant="outline"
                  size="sm"
                  :disabled="!card.available"
                  @click="setUp(card)"
                >
                  {{ t('SETTINGS_CHANNELS.SET_UP') }}
                </Button>
              </ItemActions>
            </Item>
          </li>
        </ul>
      </div>
    </template>
  </SettingsLayout>
</template>
